import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Autocomplete, TextField } from '@mui/material'

import { MenuOption } from '@/components/componentLibrary/ButtonMenu'
import { HeaderBar } from '@/components/curb/HeaderBar'
import { FilterGroup } from '@/components/curb/HeaderBar/FilterGroup'

import { useDashboardDownload } from '../dashboardDownloadContext'

export interface DashboardSlide {
  value: string
  text: string
}

interface DashboardHeaderProps {
  /** The name of the group of dashboards, for example "Time and Distance". */
  title: string
  /** The slides of the group, in order. */
  slides: DashboardSlide[]
  activeIndex: number
  onSelect: (index: number) => void
}

/** The filter pop-up: a list of the slides, in the style of the filters of the other pages. */
const SlideFilter: FC<Omit<DashboardHeaderProps, 'title'>> = ({
  slides,
  activeIndex,
  onSelect,
}) => {
  const { t } = useTranslation()

  return (
    <FilterGroup title={t('dashboard.viewFilters', 'View')}>
      <Autocomplete
        size="small"
        disableClearable
        data-cy="dashboard-view"
        // The pop-up is as wide as its content, up to 350px (see `FilterPopup`), so the field
        // needs a width of its own. 302px is what is left of 350px after the padding. The
        // longest slide name, "Trip Distance by Tech", fits.
        sx={{ width: 302 }}
        options={slides}
        value={slides[activeIndex]}
        isOptionEqualToValue={(option, other) => option.value === other.value}
        getOptionLabel={option => option.text}
        onChange={(_event, option) =>
          onSelect(slides.findIndex(({ value }) => value === option.value))
        }
        renderInput={params => (
          <TextField {...params} label={t('dashboard.viewSelectorLabel', 'Chart')} />
        )}
      />
    </FilterGroup>
  )
}

/**
 * The page header of a dashboard group, in the same style as the other pages: the title, a
 * filter pop-up to pick a slide, and the Download menu. There are no arrows for the previous and
 * the next slide: the pop-up does the same.
 */
export const DashboardHeader: FC<DashboardHeaderProps> = ({ title, ...slideProps }) => {
  const { t } = useTranslation()
  const downloadContext = useDashboardDownload()
  const download = downloadContext?.download

  const downloadOptions: MenuOption[] = [
    {
      disabled: !download,
      text: t('dashboard.downloadPNG', 'Download chart as png'),
      onClick: () => download?.png(),
    },
    {
      disabled: !download,
      text: t('dashboard.downloadCSV', 'Download data as csv'),
      onClick: () => download?.csv(),
    },
  ]

  return (
    <HeaderBar
      title={title}
      filters={<SlideFilter {...slideProps} />}
      downloadOptions={downloadOptions}
    />
  )
}
